import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx'

import { GET, httpOk } from 'helpers/api'
import { returnSign, formatDate, formatMoney } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"
import PeriodPicker from 'pages/parts/PeriodPicker'
import { docsPageUrl } from "helpers/apps"
import { businessToday, businessMonthStart } from 'helpers/businessDay'

// Разделы отчёта сверху вниз. section — сворачиваемый блок со статьями, result — строка итога.
// optional — блок показывается, только если хоть в одной колонке есть строки.
const LAYOUT = [
	{ type: 'section', key: 'revenue', label: 'revenue' },
	{ type: 'section', key: 'cost', label: 'cost_price' },
	{ type: 'result', key: 'gross_profit', label: 'gross_profit' },
	{ type: 'section', key: 'expenses', label: 'operating_expenses' },
	{ type: 'result', key: 'operating_profit', label: 'operating_profit' },
	{ type: 'section', key: 'other_income', label: 'other_income' },
	{ type: 'result', key: 'net_profit', label: 'net_profit', highlight: true },
	{ type: 'section', key: 'dividends', label: 'dividends', hint: 'dividends_hint', optional: true },
	{ type: 'result', key: 'retained_profit', label: 'retained_profit', after: 'dividends' },
	{ type: 'section', key: 'partners', label: 'pl_partners', hint: 'pl_partners_hint', optional: true, noTotal: true, noPercent: true },
	{ type: 'section', key: 'capex', label: 'pl_capex', hint: 'pl_capex_hint', optional: true, noPercent: true },
	{ type: 'section', key: 'settlements', label: 'settlements', hint: 'settlements_hint', optional: true, collapsed: true, noPercent: true },
]

function ProfitLoss() {
	const { t, i18n } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const detailDates = [
		{ id: null, name: t('pl_detail_none') },
		{ id: 'day', name: t('pl_detail_day') },
		{ id: 'week', name: t('pl_detail_week') },
		{ id: 'month', name: t('pl_detail_month') },
	]

	// Период можно передать ссылкой (?start_date=&end_date=) — так P&L открывают из
	// «Рентабельности блюд», чтобы сверить расходы за тот же период
	const [searchParams] = useSearchParams()
	const isDateParam = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: isDateParam(searchParams.get('start_date')) ? searchParams.get('start_date') : businessMonthStart(),
		end_date: isDateParam(searchParams.get('end_date')) ? searchParams.get('end_date') : businessToday(),
		pos_ids: reduxAccount.pos_id ? [reduxAccount.pos_id] : [],
		detail_date: null,
	})
	const [columns, setColumns] = useState([])
	const [showPercent, setShowPercent] = useState(false)
	// Как в iiko: всё раскрыто сразу, свёрнуты только справочные «Расчёты»
	const [expand, setExpand] = useState({ settlements: false })
	const [poses, setPoses] = useState([])

	async function searchWithFilter() {
		var urlParams = ""

		filterData.pos_ids.forEach(posId => {
			urlParams += returnSign(urlParams) + 'pos_ids[]=' + posId
		})
		if (filterData.pos_ids.length === 1)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_ids[0]
		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date
		if (filterData.detail_date)
			urlParams += returnSign(urlParams) + 'detail_date=' + filterData.detail_date

		if (urlParams !== "")
			setFilterDataExist(true)

		const response = await GET(`/report-profit-loss${urlParams}`, {}, { loader: true })
		if (httpOk(response)) setColumns(response.data.columns || [])
		setDropdownIsOpen(false)
	}

	// Метка строки: системная (ключ перевода) или название статьи
	function itemLabel(item) {
		return item.key ? t(item.key) : item.name
	}

	// Доли партнёров приводим к виду раздела: партнёр — строка, под ним положено / выдано / остаток
	function sectionOf(data, key) {
		if (key !== 'partners') return data?.[key]
		return {
			total: null,
			items: (data?.partners || []).map(partner => ({
				name: partner.name,
				suffix: partner.share_percent + '%',
				items: [
					{ key: 'pl_partner_due', amount: partner.due },
					{ key: 'pl_partner_taken', amount: partner.taken },
					{ key: 'pl_partner_remaining', amount: partner.remaining, strong: true },
				],
			})),
		}
	}

	// Строки отчёта из всех колонок: статьи объединяются по названию, у каждой строки — значение по колонкам
	function buildRows() {
		const rows = []
		const valueOf = (getter) => columns.map(column => getter(column.data))

		LAYOUT.forEach(block => {
			if (block.type === 'result') {
				if (block.after && !rows.some(row => row.section === block.after)) return
				rows.push({ type: 'result', id: block.key, label: t(block.label), highlight: block.highlight, values: valueOf(data => data?.[block.key]) })
				return
			}

			const itemsMap = new Map()
			columns.forEach((column, columnIndex) => {
				(sectionOf(column.data, block.key)?.items || []).forEach(item => {
					const id = item.key || 'n:' + item.name
					if (!itemsMap.has(id)) {
						itemsMap.set(id, { item, values: columns.map(() => null), children: new Map() })
					}
					const entry = itemsMap.get(id)
					entry.values[columnIndex] = item.amount ?? null
					if (item.is_other) entry.item = { ...entry.item, is_other: true }
					;(item.items || []).forEach(child => {
						const childId = child.key || 'n:' + child.name
						if (!entry.children.has(childId)) {
							entry.children.set(childId, { item: child, values: columns.map(() => null) })
						}
						entry.children.get(childId).values[columnIndex] = child.amount ?? null
					})
				})
			})

			// Системные строки с нулём во всех колонках (доставка, НДС и т.п.) не показываем
			const items = [...itemsMap.values()].filter(entry =>
				!entry.item.key || entry.item.key === 'trade_revenue' || entry.values.some(value => Number(value) !== 0)
			)
			if (block.optional && items.length === 0) return

			rows.push({
				type: 'section', id: block.key, section: block.key, label: t(block.label), hint: block.hint ? t(block.hint) : null,
				noPercent: block.noPercent,
				values: block.noTotal ? columns.map(() => null) : valueOf(data => sectionOf(data, block.key)?.total),
			})
			items.forEach((entry, index) => {
				const children = [...entry.children.values()]
				const childKey = block.key + '_' + index
				rows.push({
					type: 'item', id: childKey, section: block.key, childKey, hasChildren: children.length > 0,
					label: itemLabel(entry.item) + (entry.item.suffix ? ' — ' + entry.item.suffix : ''),
					isOther: entry.item.is_other, hintKey: entry.item.hint_key, noPercent: block.noPercent,
					values: children.length > 0 && block.noTotal ? columns.map(() => null) : entry.values,
				})
				children.forEach((child, childIndex) => {
					rows.push({
						type: 'child', id: childKey + '_' + childIndex, section: block.key, parentKey: childKey,
						label: itemLabel(child.item), strong: child.item.strong, isSystem: child.item.is_system, noPercent: block.noPercent, values: child.values,
					})
				})
				// Пустая строка между категориями — как в iiko
				if (children.length > 0 && index < items.length - 1) {
					rows.push({ type: 'spacer', id: childKey + '_spacer', section: block.key })
				}
			})
		})
		return rows
	}

	const rows = buildRows()
	const multiColumn = columns.length > 1
	const revenueByColumn = columns.map(column => Number(column.data?.revenue?.total) || 0)
	const revenueTotal = revenueByColumn.reduce((sum, value) => sum + value, 0)

	function sumOf(values) {
		if (values.every(value => value === null || value === undefined)) return null
		return values.reduce((sum, value) => sum + (Number(value) || 0), 0)
	}

	function columnLabel(column) {
		const posesCount = new Set(columns.map(item => item.pos_id)).size
		const parts = []
		if (posesCount > 1) parts.push(column.pos_name)
		if (column.period_label) parts.push(column.period_label)
		return parts.join(' · ') || t('amount')
	}

	function percentOf(value, revenue) {
		if (value === null || value === undefined || !revenue) return ''
		return (Number(value) / revenue * 100).toFixed(1) + '%'
	}

	function exportToExcel() {
		const header = [t('item')]
		columns.forEach(column => {
			header.push(columnLabel(column))
			if (showPercent) header.push('%')
		})
		if (multiColumn) {
			header.push(t('total'))
			if (showPercent) header.push('%')
		}
		const sheet = [header]
		rows.forEach(row => {
			if (row.type === 'spacer') return
			const indent = row.type === 'item' ? '   ' : row.type === 'child' ? '      ' : ''
			const cells = []
			const pushCell = (value, revenue) => {
				cells.push(value === null || value === undefined ? '' : Number(value) || 0)
				if (showPercent) cells.push(row.noPercent ? '' : percentOf(value, revenue))
			}
			row.values.forEach((value, index) => pushCell(value, revenueByColumn[index]))
			if (multiColumn) pushCell(sumOf(row.values), revenueTotal)
			sheet.push([indent + row.label, ...cells])
		})

		const ws = XLSX.utils.aoa_to_sheet(sheet);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('profit_loss') + ".xlsx");
	}

	function resetFilter() {
		setFilterData({
			start_date: businessMonthStart(),
			end_date: businessToday(),
			pos_ids: reduxAccount.pos_id ? [reduxAccount.pos_id] : [],
			detail_date: null,
		})
		setFilterDataExist(false)
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function isOpen(key) {
		return expand[key] !== false
	}

	function toggleExpand(key) {
		setExpand({ ...expand, [key]: !isOpen(key) })
	}

	// Свернуть всё, если хоть что-то раскрыто, иначе раскрыть всё (включая «Расчёты»)
	function toggleAll() {
		const keys = []
		rows.forEach(row => {
			if (row.type === 'section') keys.push(row.section)
			if (row.type === 'item' && row.hasChildren) keys.push(row.childKey)
		})
		const anyOpen = keys.some(key => isOpen(key))
		const next = {}
		keys.forEach(key => { next[key] = !anyOpen })
		setExpand(next)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	useEffect(() => {
		getPoses()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Ячейка суммы; с «% к выручке» рядом отдельная колонка — доля от выручки своей колонки
	function renderValue(value, revenue, row, bold) {
		const negativeRemaining = row.strong && Number(value) < 0
		return (
			<>
				<td className={`text-end text-nowrap ${negativeRemaining ? 'text-danger' : ''}`}>
					{value === null || value === undefined ? '' : (bold ? <b>{formatMoney(value)}</b> : formatMoney(value))}
				</td>
				{showPercent &&
					<td className="text-end text-nowrap text-muted">
						{!row.noPercent && percentOf(value, revenue)}
					</td>
				}
			</>
		)
	}

	function renderValues(row, bold) {
		return (
			<>
				{row.values.map((value, index) => (
					<React.Fragment key={index}>{renderValue(value, revenueByColumn[index], row, bold)}</React.Fragment>
				))}
				{multiColumn && renderValue(sumOf(row.values), revenueTotal, row, true)}
			</>
		)
	}

	function renderRow(row) {
		if (row.type === 'spacer') {
			if (!isOpen(row.section)) return null
			return (
				<tr key={row.id} className="pl-spacer">
					<td colSpan={2 + (columns.length + (multiColumn ? 1 : 0)) * (showPercent ? 2 : 1)}></td>
				</tr>
			)
		}

		if (row.type === 'result') {
			return (
				<tr key={row.id} className={row.highlight ? 'table-active' : 'table-light'}>
					<td></td>
					<td><b className={row.highlight ? 'text-uppercase' : ''}>{row.label}</b></td>
					{renderValues(row, true)}
				</tr>
			)
		}

		if (row.type === 'section') {
			return (
				<tr key={row.id}>
					<td className="text-center cursor" onClick={() => toggleExpand(row.section)}>
						<i className={`uil ${isOpen(row.section) ? 'uil-minus' : 'uil-plus'}`}></i>
					</td>
					<td>
						<b>{row.label}</b>
						{row.hint &&
							<div className="text-muted fz-12">{row.hint}</div>
						}
					</td>
					{renderValues(row, true)}
				</tr>
			)
		}

		if (row.type === 'item') {
			return (
				<tr key={row.id} className={`${!isOpen(row.section) ? 'd-none' : ''}`}>
					<td></td>
					<td className={`ps-4 ${row.hasChildren ? 'cursor' : ''}`}
						onClick={() => row.hasChildren && toggleExpand(row.childKey)}>
						{row.hasChildren &&
							<i className={`uil ${isOpen(row.childKey) ? 'uil-angle-down' : 'uil-angle-right'} me-1`}></i>
						}
						{row.hasChildren ? <b>{row.label}</b> : row.label}
						{row.isOther &&
							<div className="text-muted fz-12">{t('pl_cat_other_hint')}</div>
						}
						{row.hintKey &&
							<div className="text-muted fz-12">{t(row.hintKey)}</div>
						}
					</td>
					{renderValues(row, false)}
				</tr>
			)
		}

		return (
			<tr key={row.id} className={`${!(isOpen(row.section) && isOpen(row.parentKey)) ? 'd-none' : ''} ${row.isSystem ? 'pl-system-row' : ''}`}>
				<td></td>
				<td style={{ 'paddingLeft': '40px' }}>
					{row.strong ? <b>{row.label}</b> : row.label}
					{/* Строка, которую считает программа, а не статья клиента */}
					{row.isSystem &&
						<span className="pl-system-badge ms-2" title={t('pl_system_row_hint')}>
							<i className="uil uil-cog me-1"></i>{t('pl_system_row')}
						</span>
					}
				</td>
				{renderValues(row, row.strong)}
			</tr>
		)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center h-100">
					{t('profit_loss')}
				</h4>
				<a className="btn-rounded btn btn-outline-primary text-nowrap" target="_blank" rel="noreferrer"
					href={docsPageUrl('finance_profit_loss', i18n.language)}>
					<i className="uil uil-book-open me-1"></i>{t('documentation')}
				</a>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													isMulti
													options={poses}
													value={poses.filter(option => filterData.pos_ids.includes(option.id))}
													onChange={(options) => setFilterData({ ...filterData, 'pos_ids': (options || []).map(option => option.id) })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('pl_detail_date')}</label>
												<Select
													options={detailDates}
													value={detailDates.find(option => option.id === filterData.detail_date)}
													onChange={(option) => setFilterData({ ...filterData, 'detail_date': option.id })}
													placeholder=""
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id ?? ''}
												/>
											</div>
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
											</div>
											<div className="form-group col-md-12">
												<div className="form-check">
													<input className="form-check-input" type="checkbox" id="pl-show-percent"
														checked={showPercent}
														onChange={(e) => setShowPercent(e.target.checked)} />
													<label className="form-check-label" htmlFor="pl-show-percent">{t('pl_percent_of_revenue')}</label>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter()}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2"
										onClick={() => exportToExcel()}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span> | </span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.pos_ids.length > 0 &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{poses.filter(pos => filterData.pos_ids.includes(pos.id)).map(pos => pos.name).join(', ')}
									</span>
								}
								{filterData.detail_date &&
									<span className="me-2">
										<b>{t('pl_detail_date')}: </b>
										{detailDates.find(option => option.id === filterData.detail_date)?.name}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered pl-table mb-0" style={multiColumn ? {} : { 'maxWidth': '750px' }}>
							<thead>
								<tr>
									<th className="text-center cursor" style={{ 'minWidth': '33px' }} onClick={() => toggleAll()}
										title={t('pl_toggle_all')}>
										<i className="uil uil-sort"></i>
									</th>
									<th style={{ 'minWidth': '320px' }}>{t('item')}</th>
									{columns.map((column, index) => (
										<React.Fragment key={index}>
											<th className="text-end text-nowrap">{columnLabel(column)}</th>
											{showPercent && <th className="text-end text-nowrap">%</th>}
										</React.Fragment>
									))}
									{multiColumn &&
										<>
											<th className="text-end text-nowrap">{t('total')}</th>
											{showPercent && <th className="text-end text-nowrap">%</th>}
										</>
									}
								</tr>
							</thead>
							<tbody>
								{rows.map(row => renderRow(row))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default ProfitLoss
